import {
  type SuppliedFilterProviderOptions,
  type ValueRowProps,
  FilterProviderType,
  type ARIAFilterSuggestion,
  type AnyBlock,
  type MutableFilterBlock,
  type FilterConfig,
  FilterValueType,
  type IndexedBlockValueItem,
} from '../types'
import {FILTER_KEYS, FILTER_VALUES} from '../constants/filter-constants'
import {RootFilterProvider} from './root'
import {NO_VALUE, getFilterValue, getLastFilterBlockValue, getNoValueSuggestion} from '../utils'
import {hasMatch, score} from 'fzy.js'

export class TypeFilterProvider extends RootFilterProvider {
  filterValues: ARIAFilterSuggestion[]

  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.type, {...options, filterTypes: {...options?.filterTypes, multiValue: true}})
    this.type = FilterProviderType.Text
    this.filterValues = FILTER_VALUES.type.map(fv => ({...fv, ariaLabel: `${fv.displayName}, ${this.displayName}`}))
  }

  async getSuggestions(filterBlock: AnyBlock | MutableFilterBlock, config: FilterConfig, caretIndex?: number | null) {
    if (!this.shouldGetSuggestions(filterBlock)) return null

    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const suggestions: ARIAFilterSuggestion[] = []

    if (hasMatch(lastValue, NO_VALUE) && this.options.filterTypes.valueless) {
      suggestions.push(getNoValueSuggestion(this.displayName, this.icon))
    }

    if (!lastValue) return this.filterValues

    for (const filterValue of this.filterValues) {
      const {value: fValue, displayName} = filterValue
      const value = getFilterValue(fValue)

      const matchesValue = !!value && hasMatch(lastValue, value)
      const matchesAlias =
        !!config.aliasMatching && filterValue.aliases && filterValue.aliases.some(alias => hasMatch(lastValue, alias))
      const matchesDisplayName = !!displayName && hasMatch(lastValue, displayName)

      if (!value || (!matchesValue && !matchesDisplayName && !matchesAlias)) continue
      filterValue.priority -= score(lastValue, value)

      suggestions.push({...filterValue, type: FilterValueType.Value})
    }

    return suggestions
  }

  async validateFilterBlockValues(values: IndexedBlockValueItem[]): Promise<IndexedBlockValueItem[]> {
    const valuesIndex: Record<number, IndexedBlockValueItem> = []
    const validations = values.map(async (v, i) => {
      const suggestion = this.filterValues.find(fv => getFilterValue(fv.value) === v.value)
      if (suggestion) {
        // We explicitly set the value to the extracted value here because the suggestion value may be different
        valuesIndex[i] = {...v, ...suggestion, valid: true, value: v.value}
      } else {
        valuesIndex[i] = {...v, valid: false}
      }
    })

    await Promise.all(validations)

    return Object.values(valuesIndex)
  }
  getValueRowProps(): ValueRowProps {
    return {text: ''}
  }
}
